import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const benefits = [
  "Ensayos breves con tesis propias sobre empresa, IA y empleo",
  "Reflexiones sobre geopolítica empresarial y cambio estructural",
  "Aprendizajes reales de construir una compañía tecnológica",
  "Ideas para directivos y fundadores que prefieren criterio a ruido",
  "Sin spam, sin relleno, sin promesas de gurú",
];

export default function Newsletter() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/newsletter/subscribe`, form);
      if (r.data.already_subscribed) {
        toast.info("Ya estás suscrito a la newsletter.");
      } else {
        setSuccess(true);
        toast.success("Suscripción confirmada. Bienvenido.");
      }
    } catch {
      toast.error("Algo no funcionó. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-16 md:pt-20">
      {/* Hero */}
      <section data-testid="newsletter-hero" className="py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-slide-up">
              <p className="label-mono text-muted-foreground mb-6">Newsletter</p>
              <h1 className="font-serif text-4xl sm:text-5xl font-normal leading-[1.1] mb-8 tracking-tight">
                Pensamiento empresarial para tiempos de automatización, concentración y cambio.
              </h1>
              <p className="text-muted-foreground leading-relaxed mb-4 text-base md:text-lg">
                Una publicación breve, con criterio. Escrita por alguien que dirige, piensa y se moja.
                Sin humo, sin autobombo, sin promesas de transformación digital.
              </p>
              <p className="text-sm text-muted-foreground mb-10">
                Frecuencia: semanal o quincenal. Solo cuando hay algo que vale la pena decir.
              </p>
              <ul className="space-y-3 mb-10">
                {benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <Check size={14} className="text-primary mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>

              {success ? (
                <div data-testid="newsletter-success" className="bg-primary/10 border border-primary/20 p-6">
                  <p className="font-serif text-xl mb-2 text-primary">Suscripción confirmada.</p>
                  <p className="text-sm text-muted-foreground">
                    Bienvenido. Recibirás el primer ensayo en breve.
                    Sin spam. Baja cuando quieras.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="newsletter-signup-form" className="max-w-sm space-y-4">
                  <div>
                    <label className="label-mono text-muted-foreground mb-2 block">Nombre</label>
                    <input
                      className="input-underline w-full"
                      placeholder="Tu nombre"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      data-testid="newsletter-name"
                    />
                  </div>
                  <div>
                    <label className="label-mono text-muted-foreground mb-2 block">Email</label>
                    <input
                      type="email"
                      className="input-underline w-full"
                      placeholder="tu@empresa.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      data-testid="newsletter-email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="newsletter-submit"
                    className="label-mono bg-primary text-primary-foreground px-8 py-3.5 hover:bg-primary/90 transition-colors disabled:opacity-60 w-full"
                  >
                    {loading ? "Enviando…" : "Suscribirme a la newsletter"}
                  </button>
                  <p className="text-xs text-muted-foreground">Sin spam. Baja cuando quieras.</p>
                </form>
              )}
            </div>

            {/* Lead magnet */}
            <div data-testid="lead-magnet" className="bg-card border border-border p-10">
              <p className="label-mono text-primary mb-4">Recurso gratuito para suscriptores</p>
              <h2 className="font-serif text-2xl md:text-3xl font-normal mb-4 leading-snug">
                12 tesis sobre IA, empleo y empresa que van a definir la próxima década.
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-8">
                Un manual breve con las ideas que estructuran el pensamiento de Eduardo sobre la
                transformación que ya está en marcha. Sin hype, sin predicciones vacías.
                Doce tesis claras, argumentadas y aplicables.
              </p>
              <ul className="space-y-3">
                {[
                  "La IA no destruye empleos, separa criterio de rutina",
                  "Human-first no es romanticismo: es ventaja competitiva",
                  "Los modelos de negocio duraderos se construyen sobre confianza",
                  "La geopolítica ya está dentro de tu empresa",
                  "El fundraising es comunicación antes que finanzas",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-xs text-muted-foreground">
                    <span className="text-primary font-mono">→</span>
                    {t}
                  </li>
                ))}
                <li className="label-mono text-muted-foreground pt-2">...y 7 más</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <blockquote className="font-serif text-2xl md:text-3xl italic font-normal max-w-2xl mx-auto leading-snug text-foreground/80">
            "Humanizar primero. Digitalizar después."
          </blockquote>
          <p className="label-mono text-muted-foreground mt-4">Eduardo Delgado · CEO y cofundador de Roams</p>
        </div>
      </section>
    </div>
  );
}
